import { useMemo } from 'react';
import { useLocation, matchPath } from '@react-router';
import { PricingApiCallSource } from '@apps/stays/types';
import { HotelSearchSource } from '@shared/constants/accommodations';
import { UUID_ROUTE_PATTERN } from '@shared/utils/uuid';

/**
 * Automatically determines the pricing API call source based on route patterns.
 *
 * Every guest page that sends a pricing request reports its own value. Before TRAV-4173 the hotel
 * search page, the two redirect steps and the three detail pages had no branch here. All six fell
 * through to `guest_accommodation` and reported the same value as the accommodations list.
 *
 * A redirect step carries `redirect` in its value, and a detail page carries `detail`. The pairing is
 * deliberate: a guest never reads a redirect step, so a redirect and the detail page it leads to are
 * two different surfaces. The shared stem lets a query join one to the other.
 *
 * Detection logic (checked in order of specificity):
 * 1. /:eventHandle/edit/planning/accommodations → admin_accommodation
 * 2. /:eventHandle/edit/accommodations → admin_accommodation
 * 3. /:eventHandle/hotel/search → guest_hotel_search
 * 4. /:eventHandle/hotel/:joyPlaceId → guest_hotel_place_redirect
 * 5. /:eventHandle/accommodation-place/:accommodationPlaceId → guest_accommodation_place_redirect
 * 6. /stays/:joyPlaceId → guest_hotel_place_detail
 * 7. /travel/:placeId → public_stays_detail
 * 8. /:eventHandle/accommodations/:accommodationPlaceId → guest_accommodation_place_detail
 * 9. /:eventHandle/accommodations?source=guest_map → guest_map
 * 10. /:eventHandle/accommodations → guest_accommodation
 *
 * Step 3 must run before step 4, because `search` also matches the `:joyPlaceId` segment.
 *
 * Steps 6 and 7 constrain the id to a UUID, matching how those routes are declared. Without the
 * constraint, step 7 would also swallow `/travel/search`, `/travel/checkout` and every other Public
 * Stays route, because they are all one segment under `/travel`.
 *
 * Admin keeps one value for both of its pages. Flat page values therefore merge admin detail-page
 * traffic into `admin_accommodation`, which is about 3% of detail-page volume. Give admin its own
 * page-level values only if that 3% turns out to matter.
 *
 * The three detail pages report through `travelOffersForAccommodationProperties`, not through the
 * queries the other pages use. That payload takes `callSource` at the top level rather than inside a
 * metadata object, so the PDP call sites set it directly.
 *
 * Still unreported: the console's hotel list. It calls `searchExtendedAccommodationPropertiesV2`,
 * whose `ExtendedAccommodationContentSearchMetadata` input has no `callSource` field at all, so
 * travel_service has to add one before joy-web can send anything.
 *
 * @param contextHint - Optional manual override to force specific context
 * @returns PricingApiCallSource enum value
 */
export const usePricingCallSource = (contextHint?: PricingApiCallSource): PricingApiCallSource => {
  const location = useLocation();

  return useMemo(() => {
    if (contextHint) {
      return contextHint;
    }

    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    if (
      matchPath(pathname, {
        path: '/:eventHandle/edit/planning/accommodations',
        exact: false,
        strict: false,
      })
    ) {
      return PricingApiCallSource.AdminAccommodation;
    }

    if (
      matchPath(pathname, {
        path: '/:eventHandle/edit/accommodations',
        exact: true,
        strict: false,
      })
    ) {
      return PricingApiCallSource.AdminAccommodation;
    }

    // Checked before the `:joyPlaceId` route below, which would otherwise match `search` as an id.
    if (
      matchPath(pathname, {
        path: '/:eventHandle/hotel/search',
        exact: true,
        strict: false,
      })
    ) {
      return PricingApiCallSource.GuestHotelSearch;
    }

    if (
      matchPath(pathname, {
        path: '/:eventHandle/hotel/:joyPlaceId',
        exact: true,
        strict: false,
      })
    ) {
      return PricingApiCallSource.GuestHotelPlaceRedirect;
    }

    if (
      matchPath(pathname, {
        path: '/:eventHandle/accommodation-place/:accommodationPlaceId',
        exact: true,
        strict: false,
      })
    ) {
      return PricingApiCallSource.GuestAccommodationPlaceRedirect;
    }

    if (
      matchPath(pathname, {
        path: `/stays/:joyPlaceId(${UUID_ROUTE_PATTERN})`,
        exact: true,
        strict: false,
      })
    ) {
      return PricingApiCallSource.GuestHotelPlaceDetail;
    }

    // The UUID guard keeps this off /travel/search, /travel/checkout and the rest of Public Stays.
    if (
      matchPath(pathname, {
        path: `/travel/:placeId(${UUID_ROUTE_PATTERN})`,
        exact: true,
        strict: false,
      })
    ) {
      return PricingApiCallSource.PublicStaysDetail;
    }

    if (
      matchPath(pathname, {
        path: '/:eventHandle/accommodations/:accommodationPlaceId',
        exact: true,
        strict: false,
      })
    ) {
      return PricingApiCallSource.GuestAccommodationPlaceDetail;
    }

    if (
      matchPath(pathname, {
        path: '/:eventHandle/accommodations',
        exact: true,
        strict: false,
      })
    ) {
      return searchParams.get('source') === HotelSearchSource.GUEST_MAP ? PricingApiCallSource.GuestMap : PricingApiCallSource.GuestAccommodation;
    }

    return PricingApiCallSource.GuestAccommodation;
  }, [contextHint, location.pathname, location.search]);
};
